import { Filter } from "@repo/icons";
import {
  CpsButtonText,
  CpsSelectorItem,
  CpsSelectorRoot,
  CpsSingleSelector,
} from "corpus";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Button } from "@/components/button";
import {
  DrawerRoot,
  DrawerTitle,
  DrawerFooter,
  DrawerContent,
} from "@/components/drawer";
import {
  FormRoot,
  FormHandlerSubmit,
  FormField,
  FormItem,
  FormControl,
  FormSubmitButton,
} from "@/components/form";

interface BillingFilterParams {
  action?: string;
  selectedFilter?: string;
}

export const BillingFilter = (): JSX.Element => {
  const params: BillingFilterParams = useSearch({ from: "/settings/billing/" });
  const router = useRouter();

  const billingFilters = [
    { label: "Todas as movimentações", value: "default" },
    { label: "Somente Assist Pro", value: "assist" },
  ];

  const formSchema = z.object({
    selectedFilter: z.string(),
  });

  type FormFields = z.infer<typeof formSchema>;
  const form = useForm<FormFields>({
    defaultValues: {
      selectedFilter: params.selectedFilter ?? "default",
    },
  });

  const handleSubmit = (data: FormFields): void => {
    const { selectedFilter } = data;

    void router.navigate({
      search: {
        ...params,
        action: undefined,
        selectedFilter: selectedFilter === "default" ? undefined : selectedFilter,
      },
      replace: true,
    });
  };

  const setDrawerDismiss = (value: boolean): void => {
    if (!value && params.action === "show-filter") {
      void router.navigate({
        search: { ...params, action: undefined },
        replace: true,
      });
    }
  };

  const openFilterDrawer = (): void => {
    void router.navigate({
      search: { ...params, action: "show-filter" },
      replace: true,
    });
  };

  const filterButtonText =
    billingFilters.find((item) => item.value === params.selectedFilter)?.label ??
    billingFilters[0].label;

  return (
    <>
      <div className="my-8 mx-1">
        <Button Icon={Filter} rounded fullWidth size="small" onClick={openFilterDrawer}>
          {filterButtonText}
        </Button>
      </div>

      <DrawerRoot open={params.action === "show-filter"} setOpen={setDrawerDismiss}>
        <DrawerContent>
          <DrawerTitle>Filtrar por</DrawerTitle>
          <FormRoot {...form}>
            <FormHandlerSubmit
              handleSubmit={handleSubmit}
              className="flex flex-col gap-4 w-full mt-8"
            >
              <FormField
                control={form.control}
                name="selectedFilter"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CpsSelectorRoot>
                        <CpsSingleSelector
                          activeBackgroundColor="secondary"
                          key="checkin"
                          fullWidth
                          {...field}
                        >
                          <div className="flex flex-col gap-3 w-full">
                            {billingFilters.map((item) => (
                              <CpsSelectorItem
                                className="min-h-[42px] w-full"
                                value={item.value}
                                key={item.value}
                                data-testid={`billing-filter-${item.value}`}
                              >
                                {item.label}
                              </CpsSelectorItem>
                            ))}
                          </div>
                        </CpsSingleSelector>
                      </CpsSelectorRoot>
                    </FormControl>
                  </FormItem>
                )}
              />
              <DrawerFooter>
                <FormSubmitButton className="w-full mt-8">Aplicar</FormSubmitButton>
                <CpsButtonText
                  onClick={() => {
                    form.reset();

                    void router.navigate({
                      search: { ...params, action: undefined, selectedFilter: undefined },
                      replace: true,
                    });
                  }}
                >
                  Limpar filtro
                </CpsButtonText>
              </DrawerFooter>
            </FormHandlerSubmit>
          </FormRoot>
        </DrawerContent>
      </DrawerRoot>
    </>
  );
};
